import {
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    TextContainer,
    List,
    LegacyStack,
    Select,
    Text,
    LegacyCard,
    Badge,
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { useAppBridge, useToast } from "@shopify/app-bridge-react";
import { Redirect } from '@shopify/app-bridge/actions';
import { useLocation, useParams } from 'react-router-dom';
import Api from '../../helpers/api';
import debounce from 'lodash.debounce';
import CustomerOnHold from "../customers/CustomerOnHold";
import {getSessionToken} from "@shopify/app-bridge-utils";
import jwtDecode from "jwt-decode";
import * as helpers from './helpers';
import {filterShippingMethods} from "../../helpers/utils";
import OpfForm from "./OpfForm";
import { clamp } from "lodash";
import {ShippingMethodSelector} from "./ShippingMethodSelector";

const AddressDetails = ({address}) => {
    const parts = [
        `${address?.company || ''}`,
        `${address?.address1 || ''}`,
        `${address?.address2 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip_code || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Defined)</span>)
}

const PriceMarker = ({ price, strikethrough }) => {
    const style = strikethrough
        ? { textDecoration: 'line-through' }
        : {};

    return (
        <Text variant="headingMd" as="h2">
            <span style={style}>${price}</span>
        </Text>
    );
}

const FinalPrice = ({ lineItem, children }) => {
    const [showInput, setShowInput] = useState(false);
    const onClick = useCallback(() => {
        setShowInput(true);
    }, []);

    let discounted = lineItem.price.discounted || '0.00';

    if (lineItem.price.discount && lineItem.price.discount.quantity > 0 && discounted > 0) {
        let totalDiscount = lineItem.price.discount.total;
        discounted = discounted - parseFloat((totalDiscount / lineItem.price.discount.quantity).toFixed(2));
    }

    const prices = Array
        .from(new Set([
            lineItem.price.unit || '0.00',
            parseFloat(discounted).toFixed(2),
        ]))
        .map((price, i, list) => ({
            price,
            strikethrough: i == 0 && list.length > 1,
        }));

    if (! showInput) {
        const style = { cursor: 'pointer' };

        return (
            <div onClick={onClick} title="Enter custom price" style={style}>
                {prices.map((props, i) => <PriceMarker {...props} key={i} />)}
            </div>
        );
    }

    return (
        <>
            {children}
        </>
    );
}

const DiscountFootnote = ({discount}) => {
    const { total } = discount;

    return (total)
        ? (<span>Discount Code: ${total}</span>)
        : null;
}

const ReserveButton = ({ lineItem, reserveItem, itemKey }) => {
    const shouldHide = (lineItem.quantity === "0" && lineItem.reservation.quantity === "0");
    const needsReservation = (lineItem.reservation.quantity !== lineItem.quantity);

    return (
        <div style={{visibility: shouldHide ? 'hidden' : 'visible'}}>
        <Button disabled={lineItem.reservation.quantity === lineItem.quantity}
                variant={needsReservation ? 'primary' : undefined}
                tone={needsReservation ? 'critical' : 'success'}
                onClick={() => reserveItem(itemKey)}
                loading={lineItem.reservation.loading}
                >{(! shouldHide && lineItem.reservation.quantity === lineItem.quantity) ? 'Reserved' : 'Unreserved'}</Button>
        </div>
    );
};

const Orders = () => {
    const toast = useToast();
    const search = useLocation().search;
    const app = useAppBridge();
    const redirect = Redirect.create(app);
    const {customerId} = useParams();
    const [locationId] = useState(
        (new URLSearchParams(search)).get('location_id') || null
    );
    const [adminUserId, setAdminUserId] = useState(null);

    const [customer, setCustomer] = useState(null);
    const [location, setLocation] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [additionalInfo, setAdditionalInfo] = useState({
        placed_by: '',
        po_number: '',
        order_notes: '',
        customer_notes: '',
    });

    const [paymentMethods, setPaymentMethods] = useState({});

    const [orderProcessingForm, setOrderProcessingForm] = useState({
        airline: '',
        destination_airport: '',
        estimated_rock_boxes: 0,
        estimated_saltwater_boxes: 0,
        estimated_dry_goods_boxes: 0,
        estimated_freshwater_boxes: 0,
        estimated_livestock_boxes: 0,
        heat_packs_per_box: 0,
        gel_packs_per_box: 0,
        wave: 0,
        contact: '',
        cutoff: '',
        estimated_arrival: '',
        estimated_weight: '',
        billing_method: 'dealer',
        credits_due: false,
        payment_method: null,
        terms_code: null,
        payment_wire_received: false,
        payment_cc_last_4: '',
        payment_cc_pre_auth_amount: '',
        payment_cc_exp_date: '',
        payment_net: '',
        order_type_livestock: false,
        order_type_dry_goods: false,
        send_shipment: 'freight_collect',
        bill_freight_to: '',
        packing_instructions: '',
        requested_delivery_date: '',
    });

    const [searchTags, setSearchTags] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setSearching] = useState(false);
    const [errors, setErrors] = useState({});
    const [displayErrors, setDisplayErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);
    const [shippingMethod, setShippingMethod] = useState(null);
    const [config, setConfig] = useState({});

    const [opfPrefill, setOpfPrefill] = useState({});

    const updateLineItem = (key, updateFn) => {
        setLineItems(state => state.map(
            (item, index) => (key === index)
                ? updateFn({ ...item })
                : item
        ));
    }

    //This is trick that Rado provided, ask him, I don't know how it works :)
    //This function basically rounds up/down quantity to closest step
    const debounceQuantityUpdate = useCallback(debounce((key, quantity) => {
        updateLineItem(key, (item) => {
            if (!item.increment_quantity_by) {
                return item;
            }

            const step = item.increment_quantity_by;

            if (quantity > 0 && quantity < step) {
                quantity = step;
            } else if ((quantity % step) < (step / 2)) {
                quantity = quantity - (quantity % step);
            } else {
                quantity = parseInt(quantity) + parseInt(step - (quantity % step));
            }

            item.quantity = quantity.toString();
            item.price.total = helpers.calculateTotalPrice(item).toFixed(2);
            item.discount_code = null;

            return item;
        })
    }, 500), [lineItems]);

    const updateQuantity = useCallback((key, quantity) => {
        if (quantity == '') {
            quantity = 0;
        } else {
            quantity = parseInt(quantity, 10);
        }

        updateLineItem(key, item => {
            item.quantity = clamp(quantity, 0, item.available).toString();
            item.price.total = helpers.calculateTotalPrice(item).toFixed(2);
            item.discount_code = null;
            item.reserved = false;

            return item;
        });

        debounceQuantityUpdate(key, quantity);
    }, [lineItems, location]);

    const updateManualPrice = useCallback((key, price) => {
        if (price < 0) {
            price = 0;
        } else if (parseFloat(price) > parseFloat(lineItems[key].price.unit)) {
            price = lineItems[key].price.unit;
        }

        updateLineItem(key, item => {
            item.price.manual = price.toString();
            item.price.total = helpers.calculateTotalPrice(item).toFixed(2);
            item.discount_code = null;

            return item;
        });
    }, [lineItems, location]);

    // Getting the customer from the router parameter.
    useEffect(() => {
        getSessionToken(app).then(sessionToken => {
            const decodedToken = jwtDecode(sessionToken);

            if (decodedToken['sub']) {
                const id = decodedToken['sub'];
                setAdminUserId(id);
            }
        });
        Promise
            .all([
                Api.getCustomer(customerId).then(item => item.data),
                Api.getLocation(locationId).then(item => item.data),
                Api.getShippingMethods().then(results => results.data),
                Api.getConfig().then(results => results.data),
            ])
            .then(([
                customerData,
                locationData,
                config,
           ]) => {
                setCustomer(customerData.data);
                setPaymentMethods(customerData.payment_methods);
                setLocation(locationData.data);
                setAdditionalInfo({
                    ...additionalInfo,
                    customer_notes: locationData.data.notes,
                });
                setOrderProcessingForm({
                    ...orderProcessingForm,
                });
                setConfig(config);
                if (customerData.data.shipping_method)
                    setShippingMethod({
                        name: customerData.data.shipping_method?.name ?? '',
                        price: customerData.data.shipping_method?.price ?? '0',
                        key: customerData.data.shipping_method?.shopify_rate_id ?? customerData.data.shipping_method?.key //fallback for old values
                    });
            })
    }, []);

    const reserveItem = (key) => {
        updateLineItem(key, (item) => {
            item.reservation.loading = true;
            return item;
        });

        const quantity = lineItems[key].quantity;

        Api.reserveItem(lineItems[key].id, quantity)
            .then(() => {
                updateLineItem(key, (item) => {
                    item.reservation.quantity = quantity;
                    return item;
                });

                toast.show('Variant reserved');
            })
            .catch(() => {
                toast.show('Unable to reserve the variant', { isError: true });
            })
            .finally(() => {
                updateLineItem(key, (item) => {
                    item.reservation.loading = false;
                    return item;
                })
            });
    };

    const searchProducts = async () => {
        if ((!searchTags || !searchTags.length) && (!searchInput || !searchInput.length)) {
            toast.show("You need to provide at least one letter to search products.", {
                duration: 5000,
            });
            return;
        }

        setSearching(true);
        const { data } = await Api.getProducts(searchTags, searchInput, locationId)
        const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

        setHasMoreResults(data.meta.last_page > 1);

        const updatedLineItems = data.data.filter(item => ! selectedLineItems.some(({ id }) => id == item.shopify_variant_id))
            .map(variant => ({
                id: variant.shopify_variant_id,
                shopify_product_id: variant.shopify_product_id,
                quantity: `${variant.reserved_quantity || 0}`,
                title: `${variant.sku} - ${variant.product_title}`,
                price: {
                    unit: variant.price,
                    discounted: (parseFloat(variant.price) - (parseFloat(variant.price) * (customer?.discount ?? 0) / 100))
                        .toFixed(2),
                    total: 0,
                    manual: null,
                    original: variant.original_price,
                },
                image_url: variant.image_url,
                options: variant.options,
                increment_quantity_by : variant.increment_quantity_by,
                discount_code: null,
                reservation: {
                    quantity: `${variant.reserved_quantity || 0}`,
                    loading: false,
                },
                available: variant.available,
            })).concat(selectedLineItems);

        setLineItems(updatedLineItems);
        setSearching(false);
    }

    const placeDraftOrder = async () => {
        setErrors({});
        setDisplayErrors({});

        const variants = lineItems
            .filter(item => item.quantity > 0)
            .flatMap(item => {
                const quantity = parseInt(item.quantity, 10);

                if (! item.price.discount || item.price.discount.quantity === 0) {
                    return [item];
                }

                if (item.price.discount.quantity === -1 || item.price.discount.quantity == quantity) {
                    return [{
                        ...item,
                        custom_attributes: [
                            { key: '_discount_code', value: item.price.discount.discountCodes.join(',') },
                        ],
                    }];
                }

                return [
                    {
                        ...item,
                        quantity: `${item.price.discount.quantity}`,
                        custom_attributes: [
                            { key: '_discount_code', value: item.price.discount.discountCodes.join(',') },
                        ],
                    },
                    {
                        ...item,
                        price: {
                            ...item.price,
                            discount: null,
                        },
                        discount_code: null,
                        quantity: `${quantity - item.price.discount.quantity}`,
                    }
                ].map(item => ({
                    ...item,
                    price: {
                        ...item.price,
                        total: helpers.calculateTotalPrice(item),
                    },
                }));
            })
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                applied_discount: helpers.getDiscountDetails(item),
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                discounted_price: parseFloat(item.price.total) - parseFloat(item.discount_code?.total || '0'),
                original_price: parseFloat(item.price.original),
            }));

        const draftOrderData = {
            variants,
            location_id: location.id,
            total_price: lineItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.total) - parseFloat(item.discount_code?.total || '0');
            }, 0.0),
            placed_by: additionalInfo.placed_by,
            practice_name: location?.practice_name || null,
            account_number: customer?.account_number || null,
            po_number: additionalInfo.po_number,
            customer_notes: location.notes,
            order_notes: additionalInfo.order_notes,
            billing_address: helpers.formatLocationAddress(location?.billing ?? customer?.default_location?.billing, customer, location?.phone),
            shipping_address: helpers.formatLocationAddress(location?.shipping, customer, location?.phone),
            shipping_method: shippingMethod,
            user_id: adminUserId,
            orderProcessingForm: orderProcessingForm,
        };

        const reservationValid = await Api.validateCart(variants)
            .then(({ data }) => data.items.every(item => item.valid));

        if (! reservationValid) {
            toast.show('Some items are no longer available', { isError: true });
            setCreating(false);

            return null;
        }

        try {
            const { data } = await Api.placeDraftOrder( draftOrderData );

            Api.updateLocationAfterOrder(location.id, {
                notes: additionalInfo.customer_notes,
                contact: additionalInfo.placed_by,
            });

            await Api.clearCart(variants);

            return data.data;

        } catch ( error ) {
            if ( error.response.status == 422 ) {
                const submitErrors = {};
                const displayErrors = {};
                const opfErrors = {};

                Object.keys(error.response.data.errors).forEach( key => {
                    if (key.startsWith('orderProcessingForm.')) {
                        opfErrors[key.replace('orderProcessingForm.', '')] = error.response.data.errors[key][0];
                    } else {
                        submitErrors[key] = error.response.data.errors[key][0];
                    }
                    displayErrors[key] = error.response.data.errors[key][0];
                })

                if (Object.keys(opfErrors).length) {
                    submitErrors.orderProcessingForm = opfErrors;
                }

                setErrors(submitErrors);
                setDisplayErrors(displayErrors);
            } else {
                throw error;
            }
        }

        return null;
    }

    const placeAndReview = async () => {
        setCreating(true);

        let draft = null;

        try {
            draft = await placeDraftOrder();
        } catch (err) {
        }

        if (draft) {
            setTimeout(() => {
                redirect.dispatch(Redirect.Action.ADMIN_PATH, `/draft_orders/${draft.id}`);
            }, 5000);

            return;
        }

        setCreating(false);
    }

    const changeAdditionalInfo = useCallback((value, id) => {
        setAdditionalInfo({
            ...additionalInfo,
            [id]: value,
        });
    }, [additionalInfo]);

    const allItemsValid = lineItems.every(item => {
        return item.quantity === item.reservation.quantity
            && ! item.reservation.loading
    });

    return (
        <Frame>
            <Page title={`Place Order`} fullWidth>
                <Layout>
                    <CustomerOnHold customer={customer}/>

                    <Layout.Section>
                    <LegacyCard title={`Customer Account`} sectioned>
                        <DescriptionList items={[
                            {term: 'Account Number', description: customer?.account_number },
                            {term: 'Email', description: location?.shopify_customer_email || location?.tracking_email || customer?.email },
                            {term: 'Phone', description: location?.phone_number || customer?.home_phone },
                            {term: 'Name', description: `${customer?.additional_data?.first_name} ${customer?.additional_data?.last_name}` },
                            {term: 'Discount', description: `${location?.discount || 0}%` },
                            {term: 'Shipping', description: <AddressDetails address={location?.shipping} /> },
                            {term: 'Billing', description: <AddressDetails address={location?.billing || customer?.default_location?.billing} /> },
                        ]} />
                        <LegacyCard.Section>
                            <FormLayout>
                                <ShippingMethodSelector
                                    customer={customer}
                                    country={location?.country}
                                    state={location?.state}
                                    shippingMethod={shippingMethod}
                                    setShippingMethod={setShippingMethod}
                                />

                                <TextField autoComplete={'off'} multiline={3} label="Customer Notes" value={additionalInfo.customer_notes} id="customer_notes" onChange={changeAdditionalInfo} />
                                <TextField autoComplete={'on'} requiredIndicator={true} label="Who is the person who is placing this order for the customer?" id="placed_by" value={additionalInfo.placed_by} onChange={changeAdditionalInfo} />
                                <TextField autoComplete={'on'} label="PO Number" id="po_number" value={additionalInfo.po_number} onChange={changeAdditionalInfo} />
                            </FormLayout>
                            </LegacyCard.Section>
                    </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard title="Line Items">

                            <LegacyCard.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                            <TextField autoComplete={'on'} label="Tags" value={searchTags} onChange={setSearchTags} clearButton onClearButtonClick={() => setSearchTags('')} />
                                            <TextField autoComplete={'on'} label="Search" value={searchInput} onChange={setSearchInput} clearButton onClearButtonClick={() => setSearchInput('')} />
                                            <Button  submit loading={isSearching} variant="primary">Search</Button>
                                    </FormLayout>
                                </Form>
                            </LegacyCard.Section>

                        {lineItems.length > 0 && (
                            <>
                                <LegacyCard.Section title="Line Items">
                                    <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                        <thead>
                                            <tr>
                                                <th className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left'}}>Item</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '120px' }}>Available</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '120px' }}>Quantity</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '140px' }}>Unit Price</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '80px' }}>Final Price</th>
                                                <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {lineItems.map( (line_item, key) => {
                                                return (
                                                    <tr key={line_item.id}>
                                                        <td className="Polaris-DataTable__Cell"><Thumbnail source={line_item.image_url} /></td>
                                                        <td className="Polaris-DataTable__Cell" style={{whiteSpace: 'normal'}}>
                                                            <div>
                                                                {line_item.title}
                                                                {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                            </div>

                                                            {parseFloat(line_item.price?.discount?.total || '0') && (
                                                                <Text  variant="bodyMd" as="span" tone="subdued">
                                                                    <DiscountFootnote discount={line_item.price?.discount} />
                                                                </Text>
                                                            ) || null}
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            {line_item.available}
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            {
                                                                line_item.available > 0
                                                                    ? (
                                                                        <TextField autoComplete={'off'}
                                                                                   value={line_item.quantity}
                                                                                   min={0}
                                                                                   max={line_item.available}
                                                                                   step={line_item.increment_quantity_by ?? 1}
                                                                                   type="number"
                                                                                   onChange={quantity => updateQuantity(key, quantity)}
                                                                        />
                                                                    )
                                                                    : <Badge size="small">N/A</Badge>
                                                            }
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <FinalPrice lineItem={line_item}>
                                                                <TextField type="number"
                                                                           autoComplete={'off'}
                                                                           prefix="$"
                                                                           value={(line_item.price.manual === null) ? line_item.price.discounted : line_item.price.manual}
                                                                           min="0"
                                                                           max={line_item.price.unit}
                                                                           onChange={price => updateManualPrice(key, price)}/>
                                                            </FinalPrice>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <Text variant="headingMd" as="h2">${(parseFloat(line_item.price.total) - parseFloat(line_item?.price?.discount?.total ?? 0)).toFixed(2)}</Text>
                                                        </td>
                                                        <td className="Polaris-DataTable__Cell">
                                                            <ReserveButton lineItem={line_item} reserveItem={reserveItem} itemKey={key} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className="Polaris-DataTable__Cell" colSpan="2"><Text variant="headingMd" as="h2">Totals</Text></td>
                                                <td className="Polaris-DataTable__Cell">
                                                    <Text variant="headingMd" as="h2">
                                                        {lineItems.reduce((acc, item) => {
                                                                acc += parseInt(item.quantity) ;
                                                                return acc;
                                                            }, 0)}
                                                    </Text>
                                                </td>
                                                <td className="Polaris-DataTable__Cell">&nbsp;</td>
                                                <td className="Polaris-DataTable__Cell">
                                                    <Text variant="headingMd" as="h2">
                                                        {`$${ lineItems.reduce((acc, item) => {
                                                            return acc + parseFloat(item.price.total) - parseFloat(item?.price?.discount?.total ?? 0);
                                                        }, 0.0).toFixed(2)}`}
                                                    </Text>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </LegacyCard.Section>
                            </>
                        )}

                        {hasMoreResults && (
                            <LegacyCard.Section>
                                <Banner tone="info">
                                    <p>There are more matching products. Please narrow down the search query.</p>
                                </Banner>
                            </LegacyCard.Section>
                        )}

                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard sectioned title="Additional Information">
                            <FormLayout>
                                <TextField autoComplete={'off'} multiline={3} label="Order Notes" value={additionalInfo.order_notes} id="order_notes" onChange={changeAdditionalInfo} />
                            </FormLayout>
                        </LegacyCard>

                        <LegacyCard sectioned title="Order Processing Form">
                            <OpfForm
                                form={orderProcessingForm}
                                setForm={setOrderProcessingForm}
                                loading={creating}
                                config={config}
                                errors={errors.orderProcessingForm ?? {}}
                                opfPrefill={customer?.opf ?? {}}
                                paymentMethods={paymentMethods}
                                customerName={`${customer?.additional_data?.first_name} ${customer?.additional_data?.last_name}`}
                            />
                        </LegacyCard>
                    </Layout.Section>
                    { customer?.cards == null && (
                        <Layout.Section>
                        <Banner
                            title="No credit card added to this customer"
                            tone="warning"
                            >
                            <TextContainer>
                                <p>The customer you are placing an order for doesn't have a credit card added to his profile. Please add a credit card first.</p>
                            </TextContainer>
                            </Banner>
                        </Layout.Section>
                    )}
                    { Object.keys(displayErrors).length > 0 && (
                        <Layout.Section>
                        <Banner
                            title="There were some errors creating the order."
                            tone="critical"
                            >
                                <List>
                                    {Object.keys(displayErrors).map(key => <List.Item><strong>{key.replace('_', ' ')}</strong>: {displayErrors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        </Layout.Section>
                    )}

                    <CustomerOnHold customer={customer}/>
                </Layout>

                <PageActions
                    primaryAction={[
                        {
                            content: 'Place Order and Review',
                            onAction: placeAndReview,
                            loading: creating,
                            disabled: ! allItemsValid,
                        },
                    ]}
                />
            </Page>
        </Frame>
    );
}

export default Orders;
